import { useSearchParams } from 'next/navigation';
import { useAtomValue, useSetAtom } from 'jotai';

import { searchParamsToObject } from '@packages/shared/src/utils/searchParamsToObject/searchParamsToObject';
import { usePushQueryChange } from '@packages/shared/src/hooks/usePushQueryChange/usePushQueryChange';
import type { FragmentType } from '@packages/gql/generated/shopping';
import type { GenericTrackingEvent } from '@packages/tracking/src/types/events';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import { unmask } from '@packages/gql/src/betterMasking';
import type { UseDisplayModeFragmentFragmentDoc } from '@packages/gql/generated/shopping/UseDisplayModeFragmentFragmentDoc';
import type { ProductListDisplayMode } from '@packages/gql/generated/shopping/graphql';

import { type DesktopDisplayMode } from '../types';
import { encodeDisplayMode, decodeDisplayMode } from '../queryEncoding';
import { displayModeAtom } from '../atoms/displayMode';

/* GraphQL */ `
  fragment UseDisplayModeFragment on SearchProductResult {
    defaultDisplayMode
  }
`;

const gtmMapping = {
  cards: 'galerie_4_spaltig',
  largeCards: 'galerie_3_spaltig',
  moreInformation: 'liste',
};

const getInternalDisplayMode = (apiDisplayMode: ProductListDisplayMode): DesktopDisplayMode => {
  switch (apiDisplayMode) {
    case 'LARGE_CARDS':
      return 'largeCards';
    case 'MORE_INFORMATION':
      return 'moreInformation';
    case 'CARDS':
    default:
      return 'cards';
  }
};

const resolveDefaultDisplayMode = (
  maskedData: FragmentType<typeof UseDisplayModeFragmentFragmentDoc>,
): DesktopDisplayMode => {
  const { defaultDisplayMode } = unmask<typeof UseDisplayModeFragmentFragmentDoc>(maskedData);

  return getInternalDisplayMode(defaultDisplayMode);
};

const resolveUrlDisplayMode = (
  maskedData: FragmentType<typeof UseDisplayModeFragmentFragmentDoc>,
  searchParams: URLSearchParams | null,
): DesktopDisplayMode =>
  decodeDisplayMode(searchParamsToObject(searchParams)) ?? resolveDefaultDisplayMode(maskedData);

export const useDisplayModeValue = (
  maskedData: FragmentType<typeof UseDisplayModeFragmentFragmentDoc>,
): DesktopDisplayMode => {
  const searchParams = useSearchParams();
  const globalDisplayMode = useAtomValue(displayModeAtom);

  return globalDisplayMode ?? resolveUrlDisplayMode(maskedData, searchParams);
};

export const useDisplayMode = (
  maskedData: FragmentType<typeof UseDisplayModeFragmentFragmentDoc>,
) => {
  const dispatchGTMEvent = useTracking();
  const { pushQueryChange } = usePushQueryChange({ trailingSlash: true });

  const displayMode = useDisplayModeValue(maskedData);
  const defaultDisplayMode = resolveDefaultDisplayMode(maskedData);
  const setGlobalDisplayMode = useSetAtom(displayModeAtom);

  const pushDisplayModeChange = (newDisplayMode: DesktopDisplayMode) => {
    setGlobalDisplayMode(newDisplayMode);

    // default display mode should not be visible in the URL
    const cleanDisplayMode = newDisplayMode === defaultDisplayMode ? undefined : newDisplayMode;

    pushQueryChange(encodeDisplayMode(cleanDisplayMode), { shallow: true });

    dispatchGTMEvent<GenericTrackingEvent>({
      event: 'suche-event',
      eventValue: `view=${gtmMapping[newDisplayMode]}`,
    });
  };

  return {
    displayMode,
    defaultDisplayMode,
    pushDisplayModeChange,
  };
};
